import { useCallback } from 'react'

import { useNavigateToFrame } from './useNavigateToFrame'
import { useNavigateToSection } from './useNavigateToSection'
import type {
  CanvasEntityKind,
  GoToArtboardEventActionType,
} from '@sketch/canvas'

/**
 * Opens a canvas entity - a frame or a Section - through whichever route suits
 * it.
 *
 * Both underlying hooks are Preview-aware, navigating through the matching
 * preview route inside a Preview. The choice between them was written out at
 * three separate call sites: the view-mode overlay props, the view-mode
 * keyboard shortcuts and the inspector's selection overlay.
 *
 * The kind comes in rather than being looked up here. Every caller has it - the
 * overlays hold the node and already ask it - and resolving it from the file's
 * Section list instead put the same derivation in two hooks.
 */
export function useNavigateToEntity() {
  const navigateToFrame = useNavigateToFrame()
  const navigateToSection = useNavigateToSection()

  return useCallback(
    (
      uuid: string,
      kind: CanvasEntityKind,
      actionType: GoToArtboardEventActionType
    ) => {
      if (kind === 'section') {
        navigateToSection(uuid, actionType)
      } else {
        navigateToFrame(uuid, actionType)
      }
    },
    [navigateToFrame, navigateToSection]
  )
}
